const ICONS = Object.freeze({
  archive: 'fa-box-archive',
  backward: 'fa-backward-step',
  clone: 'fa-clone',
  delete: 'fa-trash-can',
  edit: 'fa-pen-to-square',
  forward: 'fa-forward-step',
  microphone: 'fa-microphone',
  new: 'fa-plus',
  open: 'fa-right-from-bracket',
  pause: 'fa-pause',
  play: 'fa-play',
  save: 'fa-floppy-disk',
  settings: 'fa-gear',
  stop: 'fa-stop',
});

const PURPOSES = Object.freeze({
  archive: 'ui-action--archive',
  destructive: 'ui-action--destructive',
  execution: 'ui-action--execution',
  mutation: 'ui-action--mutation',
  navigation: 'ui-action--navigation',
});

const EMPHASES = Object.freeze({
  primary: 'ui-action--primary',
  secondary: 'ui-action--secondary',
  quiet: 'ui-action--quiet',
});

/** Shared factory for controls added after Django has rendered the page. */
export function createAction(options = {}) {
  const {
    label,
    purpose = 'mutation',
    emphasis = 'secondary',
    icon = '',
    iconOnly = false,
    type = 'button',
    attributes = {},
  } = options;
  const button = document.createElement('button');
  button.type = type;
  button.className = [
    'ui-action',
    PURPOSES[purpose] || PURPOSES.mutation,
    EMPHASES[emphasis] || EMPHASES.secondary,
    iconOnly ? 'ui-action--icon-only' : '',
  ]
    .filter(Boolean)
    .join(' ');
  if (icon && ICONS[icon]) {
    const glyph = document.createElement('i');
    glyph.className = `ui-action__icon fa-solid ${ICONS[icon]}`;
    glyph.setAttribute('aria-hidden', 'true');
    button.append(glyph);
  }
  if (!iconOnly) {
    const text = document.createElement('span');
    text.className = 'ui-action__label';
    text.textContent = label;
    button.append(text);
  } else {
    button.setAttribute('aria-label', label);
    button.title = label;
  }
  Object.entries(attributes).forEach(([name, value]) => {
    if (value !== null && value !== undefined) button.setAttribute(name, String(value));
  });
  return button;
}

export function createFieldWrapper({ id, label, control, help = '', error = '', required = false }) {
  const wrapper = document.createElement('div');
  wrapper.className = `ui-field${error ? ' ui-field--invalid' : ''}`;
  const title = document.createElement('label');
  title.className = 'ui-field__label';
  title.htmlFor = id;
  title.textContent = label;
  if (required) {
    const mark = document.createElement('span');
    mark.className = 'ui-field__required';
    mark.setAttribute('aria-hidden', 'true');
    mark.textContent = '*';
    title.append(mark);
  }
  control.id = id;
  control.classList.add('ui-control');
  wrapper.append(title, control);
  if (help) {
    const description = document.createElement('div');
    description.className = 'ui-field__help';
    description.id = `${id}-help`;
    description.textContent = help;
    wrapper.append(description);
    control.setAttribute('aria-describedby', description.id);
  }
  if (error) {
    const message = document.createElement('div');
    message.className = 'ui-field__errors';
    message.id = `${id}-errors`;
    message.setAttribute('role', 'alert');
    message.textContent = error;
    wrapper.append(message);
    control.setAttribute('aria-invalid', 'true');
    control.setAttribute('aria-describedby', [help ? `${id}-help` : '', message.id].filter(Boolean).join(' '));
  }
  return wrapper;
}

export function initializeTabList(root = document) {
  root.querySelectorAll('[data-ui-tabs]').forEach((tabList) => {
    const tabs = [...tabList.querySelectorAll('[role="tab"]')];
    const activate = (tab, moveFocus = false, updateVisibility = false) => {
      tabs.forEach((candidate) => {
        const active = candidate === tab;
        candidate.classList.toggle('active', active);
        candidate.setAttribute('aria-selected', String(active));
        candidate.tabIndex = active ? 0 : -1;
        const panelId = candidate.getAttribute('aria-controls');
        const panel = panelId ? document.getElementById(panelId) : null;
        if (panel) {
          panel.classList.toggle('active', active);
          panel.classList.toggle('show', active);
          panel.classList.toggle('ui-tabs__panel--active', active);
          if (updateVisibility) {
            panel.hidden = !active;
            panel.setAttribute('aria-hidden', String(!active));
          }
        }
      });
      if (moveFocus) tab.focus();
    };
    const initiallySelected = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];
    if (initiallySelected) {
      activate(initiallySelected);
    }
    tabList.addEventListener('click', (event) => {
      const tab = event.target.closest('[role="tab"]');
      if (!tab || !tabs.includes(tab)) return;
      event.preventDefault();
      activate(tab, false, true);
    });
    tabs.forEach((tab, index) => {
      tab.addEventListener('keydown', (event) => {
        let target = null;
        if (event.key === 'ArrowRight') target = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') target = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') target = 0;
        if (event.key === 'End') target = tabs.length - 1;
        if (target === null) return;
        event.preventDefault();
        activate(tabs[target], true, true);
      });
    });
  });
}

export function initializeUiInteractions(root = document) {
  initializeTabList(root);
  initializeRosterSearch(root);
  initializeEmailRepeater(root);
  initializeIncompleteSubmitDialog(root);
  initializeImagePreviews(root);
  initializeProgressBars(root);
  root.addEventListener('DOMContentLoaded', () => focusErrorSummary(root), { once: true });
}

export function initializeRosterSearch(root = document) {
  root.querySelectorAll('[data-ui-roster-search]').forEach((input) => {
    const table = root.querySelector(input.dataset.uiRosterSearch);
    const rows = table?.querySelectorAll('[data-ui-roster-row]') || [];
    if (!table || input.dataset.uiRosterSearchReady === 'true') return;
    input.dataset.uiRosterSearchReady = 'true';
    input.addEventListener('input', () => {
      const query = input.value.trim().toLocaleLowerCase();
      rows.forEach((row) => {
        row.hidden = query !== '' && !row.textContent.toLocaleLowerCase().includes(query);
      });
    });
  });
}

export function initializeEmailRepeater(root = document) {
  root.querySelectorAll('[data-ui-email-repeater]').forEach((modal) => {
    if (modal.dataset.uiEmailRepeaterReady === 'true') return;
    const container = modal.querySelector('#email-fields-container');
    const addButton = modal.querySelector('#add-email');
    const hiddenInput = modal.querySelector('#student-emails-hidden');
    if (!container || !addButton || !hiddenInput) return;
    modal.dataset.uiEmailRepeaterReady = 'true';
    addButton.dataset.emailLabel = container.querySelector('input[type="email"]')?.getAttribute('aria-label') || '';
    addButton.dataset.duplicateMessage = container.querySelector('input[type="email"]')?.dataset.duplicateMessage || '';
    addButton.dataset.removeLabel = container.querySelector('.ui-email-remove')?.getAttribute('aria-label') || '';
    const inputs = () => [...container.querySelectorAll('input[type="email"]')];
    inputs().forEach((input) => {
      input.dataset.duplicateMessage = addButton.dataset.duplicateMessage;
    });
    const bindInput = (input) => {
      input.addEventListener('input', () => {
        const duplicate = inputs().some((candidate) => candidate !== input
          && candidate.value.trim().toLocaleLowerCase() === input.value.trim().toLocaleLowerCase());
        input.setCustomValidity(duplicate ? input.dataset.duplicateMessage : '');
        update();
      });
    };
    const update = () => {
      const fields = [...container.querySelectorAll('[data-ui-email-field]')];
      const emailInputs = inputs();
      hiddenInput.value = emailInputs.map((input) => input.value).filter((value) => value.trim()).join(';');
      fields.forEach((field) => {
        const remove = field.querySelector('.ui-email-remove');
        if (remove) remove.hidden = fields.length <= 1;
      });
      addButton.disabled = fields.length >= 10;
    };
    inputs().forEach(bindInput);
    addButton.addEventListener('click', () => {
      const fields = [...container.querySelectorAll('[data-ui-email-field]')];
      if (fields.length >= 10) return;
      const field = document.createElement('div');
      field.className = 'email-field ui-email-field';
      field.dataset.uiEmailField = '';
      const input = document.createElement('input');
      input.type = 'email';
      input.required = true;
      input.className = 'ui-control';
      input.setAttribute('aria-label', addButton.dataset.emailLabel);
      input.dataset.duplicateMessage = addButton.dataset.duplicateMessage;
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'ui-action ui-action--destructive ui-action--secondary ui-action--icon-only ui-email-remove';
      remove.setAttribute('aria-label', addButton.dataset.removeLabel);
      remove.innerHTML = '<i class="ui-action__icon fa-solid fa-trash-can" aria-hidden="true"></i>';
      remove.addEventListener('click', () => {
        const remaining = field.parentElement.querySelectorAll('[data-ui-email-field]');
        const focusTarget = field.previousElementSibling?.querySelector('input[type="email"]')
          || field.nextElementSibling?.querySelector('input[type="email"]');
        if (remaining.length > 1) {
          field.remove();
          update();
          focusTarget?.focus();
        }
      });
      field.append(input, remove);
      container.append(field);
      bindInput(input);
      update();
      input.focus();
    });
    container.addEventListener('input', update);
    update();
  });
}

export function initializeIncompleteSubmitDialog(root = document) {
  root.querySelectorAll('[data-ui-incomplete-submit]').forEach((dialog) => {
    if (dialog.dataset.uiReady === 'true') return;
    dialog.dataset.uiReady = 'true';
    const cancel = dialog.querySelector('[data-ui-incomplete-cancel]');
    const continueButton = dialog.querySelector('[data-ui-incomplete-continue]');
    cancel?.addEventListener('click', () => dialog.close('cancel'));
    continueButton?.addEventListener('click', () => {
      dialog.close('continue');
      dialog.dispatchEvent(new CustomEvent('ui-confirmed', { bubbles: true }));
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close('cancel');
    });
  });
}

export function focusErrorSummary(root = document) {
  const summary = root.querySelector('[data-ui-error-summary]');
  if (summary) summary.focus();
}

export function initializeImagePreviews(root = document) {
  root.querySelectorAll('input[type="file"][data-preview-target]').forEach((input) => {
    const preview = root.querySelector(
      `[data-image-preview][id="${CSS.escape(input.id)}-preview"]`,
    );
    if (!preview) return;
    input.addEventListener('change', () => {
      const [file] = input.files || [];
      if (!file || !file.type.startsWith('image/')) return;
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        preview.src = String(reader.result);
        preview.hidden = false;
      }, { once: true });
      reader.readAsDataURL(file);
    });
  });
}

export function initializeProgressBars(root = document) {
  root.querySelectorAll('[data-ui-progress]').forEach((bar) => {
    const percent = Number(bar.dataset.uiProgress);
    const buckets = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
    if (!Number.isFinite(percent)) return;
    bar.classList.add(`ui-progress-${buckets.reduce((closest, value) => Math.abs(value - percent) < Math.abs(closest - percent) ? value : closest, 0)}`);
  });
}
